import React, { useEffect, useState } from "react";
import {
  Button,
  Form,
  Input,
  Modal,
  Select,
  Typography,
} from "antd";
import { UsimSkuItem } from "../../../../api/usim/uploads/products/usimSkuItem";

const { TextArea } = Input;

interface ProductManageModalProps {
  isVisible: boolean;
  onClose: () => void;
  onSubmit: (data: UsimSkuItem) => Promise<void>;
  isUpdate: boolean;
  data?: UsimSkuItem;
  productTypeOptions: { label: string; value: string; id: string }[];
}

export default function ProductManageModal({
  isVisible,
  onClose,
  onSubmit,
  isUpdate,
  data,
  productTypeOptions,
}: ProductManageModalProps) {
  const [form] = Form.useForm();
  // Reset form when modal opens/closes or data changes
  useEffect(() => {
    if (isVisible) {
      if (isUpdate && data) {
        form.setFieldsValue({
          productType: data.typeName,
          planName: data.name,
          description: data.description || "",
        });
      } else {
        // Reset form and set default values for add mode
        form.resetFields();
        form.setFieldsValue({
          productType: undefined,
          planName: "",
          description: "",
        });
      }
    }
  }, [isVisible, isUpdate, data, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const selectedOption = productTypeOptions.find(opt => opt.value === values.productType);

      const usimSku: UsimSkuItem = {
        id : "",
        typeName: values.productType,
        description: values.description || "",
        name: values.planName,
        typeId: selectedOption?.id || "",
      };

      if (isUpdate && data?.id) {
        usimSku.id = data.id;
      }

      await onSubmit(usimSku);
      
      // Reset form after successful submission
      form.resetFields();
      onClose();
    } catch (error) {
      console.error('Form validation failed:', error);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  return (
    <Modal
      title={isUpdate ? "Edit Product Plan" : "Add Product Plan"}
      open={isVisible}
      onOk={handleSubmit}
      onCancel={handleCancel}
      okText={isUpdate ? "Update" : "Add"}
      cancelText="Cancel"
      destroyOnClose={true}
      maskClosable={false}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          productType: undefined,
          planName: "",
          description: "",
        }}
      >
        <Form.Item
          label="Product Type"
          name="productType"
          rules={[
            { required: true, message: "Product type is required" }
          ]}
        >
          <Select
            options={productTypeOptions}
            placeholder="Select product type"
            allowClear
            showSearch
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
          />
        </Form.Item>
        
        <Typography.Text type="secondary">
          Please follow our SKU mapping used for Shopee/Lazada
        </Typography.Text>
        <br />
        <br />

        <Form.Item
          label="Plan Name"
          name="planName"
          rules={[
            { required: true, message: "Plan name is required" },
            { whitespace: true, message: "Plan name cannot be empty" }
          ]}
        >
          <Input 
            placeholder="Enter plan name" 
            allowClear
          />
        </Form.Item>

        <Form.Item
          label="Description"
          name="description"
        >
          <TextArea
            rows={3}
            placeholder="Enter description (optional)"
            allowClear
          />
        </Form.Item>
      </Form>
    </Modal>
  );
}